import { useMemo } from 'react'

import { useMutation } from '@apollo/client'

import { UpdateAutoscanSettingDocument } from '$extensionSrc/__generated__/graphql'
import { getClient } from '$extensionSrc/apollo'
import { PLATFORM } from '$extensionSrc/utils/enums'

import { User } from './user'

export type AutoscanSetting = {
  platform: PLATFORM
  platformUserId: string
  enabled: boolean
}

export function useAutoscanSettings(
  user: User | null | undefined,
): AutoscanSetting[] | null | undefined {
  return useMemo(() => {
    if (user === undefined) {
      return undefined
    }

    if (user === null) {
      return null
    }

    return user.autoscanSettings || []
  }, [user])
}

export function isAutoscanOn(
  autoscanSettings: AutoscanSetting[],
  platform: PLATFORM,
  platformUserId: string,
): boolean {
  if (autoscanSettings === undefined) {
    return undefined
  }

  if (autoscanSettings === null) {
    return null
  }

  const settingValue = autoscanSettings.find(
    (setting) =>
      setting.platform === platform &&
      setting.platformUserId === platformUserId,
  )?.enabled

  if (settingValue === undefined || settingValue === null) {
    return true // Default to true if not set
  }

  return settingValue
}

export function useUpdateAutoscanSetting(
  platform: PLATFORM,
  platformUserId: string,
  enabled: boolean,
) {
  const [updateAutoscanSettingRequest, { data, error }] = useMutation(
    UpdateAutoscanSettingDocument,
    { client: getClient() },
  )
  const responseData = data?.updateAutoscanSetting
  const errorMessage = error?.message

  const updateAutoscanSettingOnServer = async () =>
    updateAutoscanSettingRequest({
      variables: {
        enabled,
        platform,
        platformUserId,
      },
    })

  return {
    updateAutoscanSettingOnServer,
    responseData,
    errorMessage,
  }
}
